import { isPossiblePhoneNumber } from "react-phone-number-input";
import { z } from "zod";
import type { TemplateSettingsSchemaT } from "./quickstart-settings.ts";

export const RoleZ = z
    .enum(["PATIENT", "CLINICIAN", "ADMIN", "SECRETARY", "MANAGER", "VALIDATOR", ""])
    .default("PATIENT")
    .transform((val) => val?.toLowerCase());
export type RoleT = z.infer<typeof RoleZ>;

export const UuidZ = z.string().uuid();
export type UuidT = z.infer<typeof UuidZ>;

export const EmailZ = z.string().email().min(1, "Email is required");
export type EmailT = z.infer<typeof EmailZ>;

export const PasswordZ = z.string().min(1, "Password is required").min(8, "Password must have more than 8 characters");
export type PasswordT = z.infer<typeof PasswordZ>;

export const AuthStatusZ = z.enum(["loggedIn", "loggedOut"]);
export type AuthStatusT = z.infer<typeof AuthStatusZ>;

export const LoginSchemaZ = z.object({
    email: EmailZ,
    password: PasswordZ,
});

export type LoginSchemaT = z.infer<typeof LoginSchemaZ>;

export const PhoneZ = z.string().refine((value) => isPossiblePhoneNumber(value), {
    message: "Invalid phone number",
});

export type PhoneT = z.infer<typeof PhoneZ>;

export const TokenZ = z.string().refine((value) => /^[0-9]{6}$/.test(value), {
    message: "Invalid verification code. Code should be exactly 6 digits.",
});

export type TokenT = z.infer<typeof TokenZ>;

export const titleOptions = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Prof"];
export const TitleZ = z.enum(titleOptions as [string, ...string[]]);
export type TitleT = z.infer<typeof TitleZ>;

export const FirstNameZ = z.string().min(1, "First Name is required");
export type FirstNameT = z.infer<typeof FirstNameZ>;

export const LastNameZ = z.string().min(1, "Last Name is required");
export type LastNameT = z.infer<typeof LastNameZ>;

export type OrganisationSummary = {
    organisation_id: string;
    organisation_name: string;
};

/** Query-string fields passed through on /signup for invite and quickstart flows */
export type SignupInviteSearchFields = {
    /** Prefill or SMS-first signup (`?phone=`) */
    phone?: string;
    /** Duplicated from `organisation` for auth triggers that read flat keys */
    organisation_id?: string;
    organisation_name?: string;
    hospital_number?: string;
    invited_by?: string;
    injecting_clinician_id?: string;
    referring_clinician_id?: string;
    operating_clinician_id?: string;
    shockwaving_clinician_id?: string;
    /** User id of the clinician who provided education (invite URL) */
    educated_by?: string;
    contact_id?: string;
    name?: string;
    description?: string;
    trackers?: string;
    diary_labels?: string;
    required_events?: string;
    /** JSON array string of template consent selections from invite URL; parsed in webhook */
    consents?: string;
    orthopaedic_area?: string;
    surgery_option_id?: string;
    quickstart_id?: string;
    quickstart_key?: string;
    study_invite_id?: string;
    /** Short QR diary invite id - settings hydrated via public-select-user-metadata */
    diary_invite_id?: string;
    /** Optional specialty pin from study clinician invite URL */
    specialty?: string;
    /** JSON array string from invite URL; parsed in webhook */
    injection_details?: string;
    injection_method_id?: string;
    injection_id?: string;
    /** When true, forms must be completed in tracker array order */
    sequential_forms?: boolean;
    /**
     * Template override for hospital number collection.
     * When omitted, diary create uses the organisation `nhs_flag`.
     */
    require_hospital_number?: boolean | null;
    /** BCP-47 programme locale from template / invite URL */
    locale?: string;
};

export type UserMetadata = {
    role?: RoleT;
    email?: string;
    organisation?: OrganisationSummary;
    quickstart?: TemplateSettingsSchemaT;
} & SignupInviteSearchFields;
